import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Container, IconButton } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ArrowRight, Star, Users, BookOpen, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SLIDE_DURATION_MS = 8000;
const TOTAL_SLIDES = 3;
const YOUTUBE_VIDEO_ID = 'tqPm45n5LDU';
// YouTube requires mute=1 for autoplay to work in most browsers
const YOUTUBE_EMBED_SRC = `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&mute=1&controls=1&loop=1&playlist=${YOUTUBE_VIDEO_ID}&rel=0&modestbranding=1`;

const Hero: React.FC = () => {
  const navigate = useNavigate();
  const [slideIndex, setSlideIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const HERO_SLIDE_3_IMAGE = '/hero-slide-3.png';
  const totalSlidesRef = useRef(TOTAL_SLIDES);

  useEffect(() => {
    const t = setInterval(() => {
      setDirection(1);
      setSlideIndex((prev) => (prev + 1) % totalSlidesRef.current);
    }, SLIDE_DURATION_MS);
    return () => clearInterval(t);
  }, []);

  const goToSlide = (index: number) => {
    setDirection(index > slideIndex ? 1 : -1);
    setSlideIndex(index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: 'easeOut' as any },
    },
  };

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 120 : -120, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? -120 : 120, opacity: 0 }),
  };

  const goTo = (delta: number) => {
    const next = (slideIndex + delta + TOTAL_SLIDES) % TOTAL_SLIDES;
    setDirection(delta >= 0 ? 1 : -1);
    setSlideIndex(next);
  };

  return (
    <Box
      id="hero"
      sx={{
        minHeight: '90vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        pt: 10,
        pb: 8,
        background: 'transparent',
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Slider navigation arrows */}
        <IconButton
          onClick={() => goTo(-1)}
          sx={{
            position: 'absolute',
            left: { xs: 4, md: -20 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            color: '#94a3b8',
            bgcolor: 'rgba(15, 23, 42, 0.6)',
            '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.3)', color: '#f1f5f9' },
          }}
        >
          <ChevronLeft size={28} />
        </IconButton>
        <IconButton
          onClick={() => goTo(1)}
          sx={{
            position: 'absolute',
            right: { xs: 4, md: -20 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            color: '#94a3b8',
            bgcolor: 'rgba(15, 23, 42, 0.6)',
            '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.3)', color: '#f1f5f9' },
          }}
        >
          <ChevronRight size={28} />
        </IconButton>

        <AnimatePresence initial={false} mode="wait" custom={direction}>
          <motion.div
            key={slideIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4 }}
            style={{ width: '100%' }}
          >
          {slideIndex === 0 && (
              <motion.div variants={containerVariants} initial="hidden" animate="visible">
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'center', gap: 6 }}>
                  <Box sx={{ width: { xs: '100%', lg: '50%' }, textAlign: { xs: 'center', lg: 'left' } }}>
                    <motion.div variants={itemVariants}>
                      <Typography
                        variant="h1"
                        className="section-title"
                        sx={{
                          fontSize: { xs: '2.25rem', md: '3.5rem', lg: '4rem' },
                          fontWeight: 700,
                          lineHeight: 1.2,
                          mb: 2,
                          color: '#f1f5f9',
                        }}
                      >
                        Master the Future of{' '}
                        <Box
                          component="span"
                          sx={{
                            background: 'linear-gradient(135deg, #60a5fa, #93c5fd)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}
                        >
                          Technology
                        </Box>
                      </Typography>
                    </motion.div>
                    <motion.div variants={itemVariants}>
                      <Typography
                        variant="h5"
                        sx={{
                          color: '#cbd5e1',
                          mb: 4,
                          fontSize: { xs: '1.1rem', md: '1.3rem' },
                          lineHeight: 1.6,
                          maxWidth: '500px',
                          mx: { xs: 'auto', lg: 0 },
                        }}
                      >
                        Learn Python Programming, Robotics, AI, Machine Learning, Gen AI, and Data Science
                        through interactive content and hands-on projects.
                      </Typography>
                    </motion.div>
                    <motion.div variants={itemVariants}>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4, justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                        <Button
                          variant="contained"
                          size="large"
                          endIcon={<ArrowRight size={20} />}
                          className="btn-primary"
                          sx={{
                            background: '#2563EB',
                            color: '#fff',
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: '30px',
                            textTransform: 'none',
                            '&:hover': { background: '#1D4ED8' },
                          }}
                        >
                          Start Learning
                        </Button>
                        <Button
                          variant="outlined"
                          size="large"
                          startIcon={<Play size={20} />}
                          onClick={() => goToSlide(1)}
                          sx={{
                            borderColor: '#93c5fd',
                            borderWidth: 2,
                            color: '#93c5fd',
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: '30px',
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: '#bfdbfe',
                              backgroundColor: 'rgba(147, 197, 253, 0.12)',
                              color: '#bfdbfe',
                              borderWidth: 2,
                            },
                          }}
                        >
                          Watch Demo
                        </Button>
                      </Box>
                    </motion.div>
                    <motion.div variants={itemVariants}>
                      <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Star size={20} color="#fbbf24" />
                          <Typography variant="body1" sx={{ color: '#94a3b8', fontWeight: 500 }}>4.9/5 Rating</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Users size={20} color="#2563EB" />
                          <Typography variant="body1" sx={{ color: '#94a3b8', fontWeight: 500 }}>10K+ Students</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BookOpen size={20} color="#2563EB" />
                          <Typography variant="body1" sx={{ color: '#94a3b8', fontWeight: 500 }}>500+ Courses</Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  </Box>
                  <Box sx={{ width: { xs: '100%', lg: '50%' }, display: 'flex', justifyContent: 'center' }}>
                    <motion.div variants={itemVariants} style={{ position: 'relative', width: '100%', maxWidth: '600px' }}>
                      <motion.div
                        animate={{ y: [0, -8, 0] }}
                        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                        whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '16/9',
                            borderRadius: '18px',
                            overflow: 'hidden',
                            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.06)',
                            border: '1px solid #E5E7EB',
                            background: '#FFFFFF',
                            '&:hover': { boxShadow: '0 12px 28px rgba(0, 0, 0, 0.08)' },
                          }}
                        >
                          <iframe
                            width="100%"
                            height="100%"
                            src="https://www.youtube.com/embed/fKhmlrdrS-4?autoplay=0&mute=1&controls=1&rel=0&modestbranding=1"
                            title="Reviving India - E-Learning Platform"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ borderRadius: '12px' }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0, left: 0, right: 0, bottom: 0,
                              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.04) 0%, transparent 100%)',
                              pointerEvents: 'none',
                            }}
                          />
                        </Box>
                      </motion.div>
                    </motion.div>
                  </Box>
                </Box>
              </motion.div>
          )}

          {slideIndex === 1 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Box sx={{ width: { xs: '100%', lg: '50%' }, textAlign: { xs: 'center', lg: 'left' } }}>
                  <motion.div variants={itemVariants} initial="hidden" animate="visible">
                    <Typography
                      variant="h1"
                      className="section-title"
                      sx={{
                        fontSize: { xs: '2.25rem', md: '3.5rem', lg: '4rem' },
                        fontWeight: 700,
                        lineHeight: 1.2,
                        mb: 2,
                        color: '#f1f5f9',
                      }}
                    >
                      Shocking{' '}
                      <Box
                        component="span"
                        sx={{
                          background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        Moments
                      </Box>
                    </Typography>
                  </motion.div>
                  <motion.div variants={itemVariants} initial="hidden" animate="visible">
                    <Typography
                      variant="h5"
                      sx={{
                        color: '#cbd5e1',
                        mb: 4,
                        fontSize: { xs: '1.1rem', md: '1.3rem' },
                        lineHeight: 1.6,
                        maxWidth: '500px',
                        mx: { xs: 'auto', lg: 0 },
                      }}
                    >
                      Catch our most impactful lessons and highlights. Stay curious—watch, learn, and join our community.
                    </Typography>
                  </motion.div>
                  <motion.div variants={itemVariants} initial="hidden" animate="visible">
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowRight size={20} />}
                      onClick={() => navigate('/contact')}
                      sx={{
                        background: '#f59e0b',
                        color: '#fff',
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: '30px',
                        textTransform: 'none',
                        '&:hover': { background: '#d97706' },
                      }}
                    >
                      Join us
                    </Button>
                  </motion.div>
                </Box>
                <Box sx={{ width: { xs: '100%', lg: '50%' }, display: 'flex', justifyContent: 'center' }}>
                  <motion.div
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    style={{ position: 'relative', width: '100%', maxWidth: '600px' }}
                  >
                    <motion.div
                      animate={{ y: [0, -8, 0] }}
                      transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                      whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          aspectRatio: '16/9',
                          borderRadius: '18px',
                          overflow: 'hidden',
                          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.06)',
                          border: '1px solid #E5E7EB',
                          background: '#0f172a',
                          '&:hover': {
                            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.08)',
                            '& .hero-play-overlay': {
                              transform: 'translate(-50%, -50%) scale(1.08)',
                              boxShadow: '0 6px 28px rgba(245, 158, 11, 0.5)',
                            },
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={HERO_SLIDE_3_IMAGE}
                          alt="Shocking Moments - Reviving India"
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center',
                            display: 'block',
                            backgroundColor: '#1e293b',
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, transparent 50%)',
                            pointerEvents: 'none',
                          }}
                        />
                        {/* Centered play button overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            bgcolor: 'rgba(245, 158, 11, 0.9)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 24px rgba(245, 158, 11, 0.4)',
                            border: '3px solid rgba(255,255,255,0.9)',
                            pointerEvents: 'none',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '& .play-icon': {
                              marginLeft: 4,
                            },
                          }}
                          className="hero-play-overlay"
                        >
                          <Play size={32} color="#fff" className="play-icon" fill="#fff" />
                        </Box>
                      </Box>
                    </motion.div>
                  </motion.div>
                </Box>
              </Box>
          )}

          {slideIndex === 2 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  alignItems: 'center',
                  gap: 4,
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    width: { xs: '100%', lg: '45%' },
                    textAlign: { xs: 'center', lg: 'left' },
                    order: { xs: 2, lg: 1 },
                  }}
                >
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Sparkles size={24} color="#a78bfa" />
                    <Typography
                      variant="overline"
                      sx={{
                        color: '#a78bfa',
                        letterSpacing: 2,
                        fontWeight: 600,
                      }}
                    >
                      AI for Everyone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2rem', md: '3rem', lg: '3.5rem' },
                      fontWeight: 700,
                      lineHeight: 1.2,
                      mb: 2,
                      color: '#f1f5f9',
                    }}
                  >
                    Learn AI. Build the{' '}
                    <Box
                      component="span"
                      sx={{
                        background: 'linear-gradient(135deg, #a78bfa, #c4b5fd)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Future.
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      color: '#cbd5e1',
                      mb: 4,
                      fontSize: { xs: '1rem', md: '1.15rem' },
                      lineHeight: 1.7,
                      maxWidth: '420px',
                      mx: { xs: 'auto', lg: 0 },
                    }}
                  >
                    From fundamentals to Gen AI and robotics—hands-on courses designed so everyone can create with AI. Start your journey today.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowRight size={20} />}
                    onClick={() => navigate('/contact')}
                    sx={{
                      background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
                      color: '#fff',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: '30px',
                      textTransform: 'none',
                      boxShadow: '0 4px 14px rgba(124, 58, 237, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #6d28d9, #8b5cf6)',
                        boxShadow: '0 6px 20px rgba(124, 58, 237, 0.5)',
                      },
                    }}
                  >
                    Join us
                  </Button>
                </Box>
                <Box
                  sx={{
                    width: { xs: '100%', lg: '55%' },
                    display: 'flex',
                    justifyContent: 'center',
                    order: { xs: 1, lg: 2 },
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    style={{ width: '100%', maxWidth: '640px' }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '16/9',
                        borderRadius: '18px',
                        overflow: 'hidden',
                        boxShadow: '0 12px 40px rgba(124, 58, 237, 0.2)',
                        border: '1px solid rgba(167, 139, 250, 0.3)',
                      }}
                    >
                      <iframe
                        width="100%"
                        height="100%"
                        src={YOUTUBE_EMBED_SRC}
                        title="AI for Everyone - Reviving India"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{ borderRadius: '12px' }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0, left: 0, right: 0, bottom: 0,
                          background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.06) 0%, transparent 60%)',
                          pointerEvents: 'none',
                        }}
                      />
                    </Box>
                  </motion.div>
                </Box>
              </Box>
          )}
          </motion.div>
        </AnimatePresence>

        {/* Dots */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            mt: 4,
          }}
        >
          {Array.from({ length: TOTAL_SLIDES }, (_, i) => i).map((i) => (
            <Box
              key={i}
              onClick={() => goToSlide(i)}
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: slideIndex === i ? '#2563EB' : 'rgba(148, 163, 184, 0.5)',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': { bgcolor: slideIndex === i ? '#1D4ED8' : 'rgba(148, 163, 184, 0.8)' },
              }}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Hero;
